import { z } from "zod"
import { SuggestedMessagingArea, SuggestedMessagingAreaSchema } from "./suggested-messaging"
import { LiquidProductVariantSchema } from "../shopify/liquid/variant"
import { ShopifyIDSchema } from "../shopify"

// ---- Enums ----

export const UVInsertFaceElementTypeSchema = z.enum(["INSERT", "GREETINGS_CARD"])
export type UVInsertFaceElementType = z.infer<typeof UVInsertFaceElementTypeSchema>

export const UVInsertFaceElementLocationSchema = z.enum([
  "FRONT",
  "INSIDE_LEFT",
  "INSIDE_RIGHT",
  "BACK",
])
export type UVInsertFaceElementLocation = z.infer<typeof UVInsertFaceElementLocationSchema>

// ---- Shared sub-schemas ----

const SizeDimensionSchema = z.union([z.string(), z.number()]).transform((v) => String(v))

const SizeSchema = z.object({
  minHeight: SizeDimensionSchema.optional(),
  maxHeight: SizeDimensionSchema.optional(),
  height: SizeDimensionSchema.optional(),
  minWidth: SizeDimensionSchema.optional(),
  maxWidth: SizeDimensionSchema.optional(),
  width: SizeDimensionSchema.optional(),
})

const PositioningSchema = z.object({
  top: z.string().optional(),
  right: z.string().optional(),
  bottom: z.string().optional(),
  left: z.string().optional(),
})

/** CSS `font-size`: bare numbers from Liquid become `${n}px` in the visualiser; strings pass through (e.g. `1rem`, `14px`). */
const UVInsertTextFontSizeSchema = z.union([z.number(), z.string()])

// ---- Face element config schemas (`z.union`: TEXT static keyed vs display-only vs editable; discriminatedUnion cannot wrap refinements). ----

const UVInsertFaceElementTextStaticKeyedConfigSchema = z.object({
  type: z.literal("TEXT"),
  editable: z.literal(false),
  lineItemPropertyKey: z.string().min(1),
  /** Initial copy when `textByKey` has no value yet. */
  value: z.string().optional(),
  /** When true, suggested-message picks on this tab apply to this key (see shared view `getSuggestionTargetTextPropertyKeyForRegionTab`). */
  suggestionTarget: z.boolean().optional(),
  font: z.string(),
  fontWeight: z.string().optional(),
  fontSize: UVInsertTextFontSizeSchema.optional(),
  lineHeight: z.number().optional(),
  color: z.string().optional(),
  maxLength: z.number().int().positive(),
  maxLines: z.number().int().positive(),
  size: SizeSchema.default({}),
  positioning: PositioningSchema.default({}),
  style: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
})

const UVInsertFaceElementTextStaticDisplayConfigSchema = z
  .object({
    type: z.literal("TEXT"),
    editable: z.literal(false),
    value: z.string(),
    lineItemPropertyKey: z.string().optional().default(""),
    font: z.string(),
    fontWeight: z.string().optional(),
    fontSize: UVInsertTextFontSizeSchema.optional(),
    lineHeight: z.number().optional(),
    color: z.string().optional(),
    maxLength: z.number().int().positive(),
    maxLines: z.number().int().positive(),
    size: SizeSchema.default({}),
    positioning: PositioningSchema.default({}),
    style: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  })
  .refine((d) => !(d.lineItemPropertyKey ?? "").trim(), {
    path: ["lineItemPropertyKey"],
    message: "display-only static TEXT must omit lineItemPropertyKey or use an empty string",
  })

const UVInsertFaceElementTextEditableConfigSchema = z
  .object({
    type: z.literal("TEXT"),
    lineItemPropertyKey: z.string().min(1),
    /** Omitted or `true` in Liquid; must not be `false` (that shape uses a static TEXT schema). */
    editable: z.boolean().optional(),
    value: z.string().optional(),
    suggestionTarget: z.boolean().optional(),
    font: z.string(),
    fontWeight: z.string().optional(),
    fontSize: UVInsertTextFontSizeSchema.optional(),
    lineHeight: z.number().optional(),
    color: z.string().optional(),
    maxLength: z.number().int().positive(),
    maxLines: z.number().int().positive(),
    size: SizeSchema.default({}),
    positioning: PositioningSchema.default({}),
    style: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  })
  .refine((d) => d.editable !== false, {
    path: ["editable"],
    message: "editable: false must use static TEXT config (keyed label or display-only)",
  })

const UVInsertFaceElementImageConfigSchema = z.object({
  type: z.literal("IMAGE_UPLOAD"),
  lineItemPropertyKey: z.string().min(1),
  size: SizeSchema.default({}),
  positioning: PositioningSchema.default({}),
  style: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  backgroundColor: z.string().optional(),
  borderRadius: z.string().optional(),
})

const UVInsertFaceElementConfigSchema = z.union([
  UVInsertFaceElementTextStaticKeyedConfigSchema,
  UVInsertFaceElementTextStaticDisplayConfigSchema,
  UVInsertFaceElementTextEditableConfigSchema,
  UVInsertFaceElementImageConfigSchema,
])

/** Parsed from Liquid / DTOs (no session value/setValue). */
export type UVInsertFaceElementConfigData = z.infer<typeof UVInsertFaceElementConfigSchema>

/**
 * TEXT elements stored in `textByKey` and emitted as line item properties when non-empty.
 * Includes editable fields and read-only keyed labels; excludes display-only static TEXT (no LIP key).
 */
export function isUvInsertUserConfigurableText(
  el: UVInsertFaceElementConfigData,
): el is
  | z.infer<typeof UVInsertFaceElementTextEditableConfigSchema>
  | z.infer<typeof UVInsertFaceElementTextStaticKeyedConfigSchema> {
  return el.type === "TEXT" && Boolean(el.lineItemPropertyKey?.trim())
}

// ---- Hydrated UI types (value/setValue layered on top in the theme) ----

/**
 * Maps each config type to its strongly-typed value and setValue pair.
 * Add new entries here to extend the discriminated union automatically.
 */
type UVInsertFaceElementConfigMap = {
  TEXT: {
    value: string
    setValue: (value: string) => void
    font: string
    fontWeight?: string
    fontSize?: number | string
    lineHeight?: number
    color?: string
    maxLength: number
    maxLines: number
  }
  IMAGE_UPLOAD: {
    value: File
    setValue: (value: File) => void
    backgroundColor?: string
    borderRadius?: string
  }
}

/**
 * Shared properties across all UVInsertFaceElementConfig variants,
 * independent of the config type.
 */
type UVInsertFaceElementConfigBase = {
  /** The line item property  */
  lineItemPropertyKey: string
  size: {
    minHeight?: string
    maxHeight?: string
    height?: string
    minWidth?: string
    maxWidth?: string
    width?: string
  }
  positioning: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
  style: Record<string, string | number>
}

/**
 * A discriminated union of all possible face element config variants.
 * Each member is the base config merged with a specific type key
 * and its corresponding value/setValue types from UVInsertFaceElementConfigMap.
 *
 * Distributing over keyof UVInsertFaceElementConfigMap at the end
 * produces a union rather than a single merged type, enabling
 * TypeScript to narrow value and setValue when type is known.
 */
export type UVInsertFaceElementConfig = {
  [K in keyof UVInsertFaceElementConfigMap]: UVInsertFaceElementConfigBase & { type: K } & UVInsertFaceElementConfigMap[K]
}[keyof UVInsertFaceElementConfigMap]

/** Convenience type for text-based face element configs */
export type UVInsertFaceElementTextConfig = Extract<UVInsertFaceElementConfig, { type: "TEXT" }>

/** Convenience type for image upload face element configs */
export type UVInsertFaceElementImageConfig = Extract<UVInsertFaceElementConfig, { type: "IMAGE_UPLOAD" }>

// ---- Face element & config schemas ----

const UVInsertFaceElementSchema = z.object({
  type: UVInsertFaceElementTypeSchema,
  location: UVInsertFaceElementLocationSchema,
  elementConfig: z.array(UVInsertFaceElementConfigSchema).default([]),
  subtitle: z.string().optional(),
  color: z.string().optional(),
  backgroundColor: z.string().optional(),
})

export interface UVInsertFaceElement {
  type: "INSERT" | "GREETINGS_CARD"
  location: "FRONT" | "INSIDE_LEFT" | "INSIDE_RIGHT" | "BACK"
  /** From Liquid; use {@link UVInsertFaceElementConfig} when the theme attaches value/setValue. */
  elementConfig: UVInsertFaceElementConfigData[]
  subtitle?: string
  color?: string
  backgroundColor?: string
}

/** Core configuration object for a UVInsert */
export interface UVInsertConfig {
  /** Metaobject ID */
  id: z.infer<typeof ShopifyIDSchema>
  /** Metaobject handle */
  handle: string
  /** Whether this UV insert config is enabled for use. */
  active: boolean
  /** Theme variants (Liquid JSON) available for this UV insert */
  themes: z.infer<typeof LiquidProductVariantSchema>[]
  /** The name of the area this insert represents in the context of suggested messaging */
  suggestedMessagingArea?: z.infer<typeof SuggestedMessagingAreaSchema>
  /**
   * Human-readable description for this config, declared in uv-inserts-config.liquid.
   * Shown as the subtitle on the option selection page that leads to this config.
   * Replaces the legacy window.giftWrapData price-fetching subtitle functions in pages.js.
   */
  description?: string
  /** A list of face elements available */
  faceElements: UVInsertFaceElement[]
}

export const UVInsertConfigSchema = z.object({
  id: ShopifyIDSchema,
  handle: z.string(),
  active: z.boolean().default(true),
  themes: z.array(LiquidProductVariantSchema),
  suggestedMessagingArea: SuggestedMessagingAreaSchema.optional(),
  description: z.string().optional(),
  faceElements: z.array(UVInsertFaceElementSchema).default([]),
})


/**
 * Normalized Liquid bootstrap data after {@link readUvInsertsBootstrapPayload} reads the window.
 * `configs` and `suggestedMessaging` stay as raw JSON until DTO transforms produce domain models.
 */
export type UvInsertsBootstrapPayload = {
  configs: unknown[]
  templateIds: Record<number | string, string | null>
  suggestedMessaging?: unknown[]
}
